import axios from 'axios'
import service, { serviceGo } from "@/utils/request";
import {Popover} from "element-ui";

interface TaskTrigger {
  agent_id: number
  condition: string // JSON string
}


interface Task {
  description: string
  current_goal: string
  status?: number
  required_agent_state: string // JSON string
  triggers: TaskTrigger[]
}

export interface Dialogues {
  role_id?: number
  text: string
}

export interface RoleConfig {
  role_id?: number
  task: string
  speak_timing: string
  speak_tone: string
  character_setting: string
}

export interface PlayTarget {
  progress_indicator: string
  progress_change_rules: string
}

// v1.0 奖励结构（保留兼容）
export interface Reward {
  type: string // 奖励类型：成就、道具、好感度、事件、属性
  reward_description: string // 具体奖励描述
  reward_character_id?: number // 角色ID（好感度奖励时使用）
  attribute_name?: string // 属性名称（当type为'属性'时使用：武力值、智慧值、体力值、灵敏值、精神值、圣母值、黑化值）
  attribute_value?: number // 属性数值（当type为'属性'时使用）
}

// v2.0 通用奖励配置
export interface RewardConfig {
  type: string // "成就" / "道具" / "事件" / "属性"
  reward_description?: string // 奖励描述
  role_id?: number // 角色ID（属性奖励时使用）
  attribute_name?: string // 属性名称（属性奖励时使用）
  attribute_value?: number // 属性数值（属性奖励时使用）
}

// v2.0 积分奖励
export interface PointsReward {
  points_id: number // 积分ID
  points_gain: number // 积分增益值
}

// v2.0 好感度奖励
export interface AffectionReward {
  role_id: number // 角色ID
  affection_gain: number // 好感度增益值
}

// v2.0 进度增益
export interface ProcessGain {
  process_name: string // 进度名称
  process_gain: number // 增益值
}

// v1.0 进度配置（保留兼容）
export interface ProcessConfig {
  name: string // 进度名称，如"愤怒值"、"羞愧值"
  start_process: number // 开始进度值
  end_process: number // 结束进度值
  progress_change_rules: string // 进度指标如何提升的规则描述
  award_conf: Reward[] // 奖励配置列表
  after_story?: Array<{role_id: number, text: string}> // 后续剧情对话列表
}

// v2.0 进度配置
export interface ProcessConfigV2 {
  name: string // 进度名称
  start_process: number // 开始进度值
  end_process: number // 结束进度值
  award_conf?: RewardConfig[] // 成就/道具/事件奖励
  points_reward?: PointsReward[] // 积分奖励
  affection_reward?: AffectionReward[] // 好感度奖励
  next_node_id?: number | null // 进度完成后跳转目标节点
  ending_name?: string // 绑定的结局名称
}

// v1.0 彩蛋结构（保留兼容）
export interface Egg {
  name: string
  trigger_condition: string
  reward_type: string // 奖励类型：成就、道具、好感度、事件、属性
  reward_description: string // 具体奖励描述
  reward_character_id?: number // 角色ID（好感度奖励时使用）
  attribute_name?: string // 属性名称（当reward_type为'属性'时使用）
  attribute_value?: number // 属性数值（当reward_type为'属性'时使用）
}

// v2.0 彩蛋结构
export interface EggV2 {
  egg_name: string // 彩蛋名称
  trigger_condition?: string // 触发条件文本描述
  process?: ProcessGain[] // 进度增益
  award_conf?: RewardConfig[] // 成就/道具/事件奖励
  points_reward?: PointsReward[] // 积分奖励
  affection_reward?: AffectionReward[] // 好感度奖励
}

export interface OutputExample {
  content: string
  is_tile?: boolean
}

export interface OutputExampleJson {
  content: string
}

export interface PlotEnding {
  name: string           // 结局名称，如 "结局1"
  content: string        // 结局故事内容
  output_examples: OutputExample[]
  next_node_id?: number | null  // 结局跳转的目标节点
}

export interface UiShowNextNodeEnding {
  name: string
  next_node_id?: number | null
}

export interface UiShowNextNode {
  next_node_id?: number | null
  plot_end?: UiShowNextNodeEnding[]
}

export interface CurrentPlotNew {
  plot_start: string
  plot_process: string
  plot_end: string | PlotEnding[]  // 兼容旧格式 string，新格式为数组
  core_info?: string  // 核心信息：AI扩写【新】的输入源（随 current_plot_new 一起存）
}

export interface UserMessageNew {
  result: string        // 如果，则
  ai_reply: string      // AI回复方式
  progress_name: string // 进度指标名称（下拉单选）
  progress_value: number // 实化数值
  output_examples?: OutputExample[]  // 输出示例
}

// 选项配置
export interface NodeOption {
  text: string           // 选项显示文字
  dialogues: Dialogues[] // 预录对话
  progress_name: string  // 关联的进度指标名称
}

// 选项玩法：点击后填入输入框作为灰色前缀的选项（与上面的 NodeOption 独立）
export interface InputOption {
  content: string        // 选项文案
}

// 进入条件
export interface EntryCondition {
  type: string // '货币' | '属性' | '好感度' | '道具' | '服装'
  // 货币
  value?: number
  is_free?: boolean
  // 属性
  attribute_name?: string
  attribute_value?: number
  // 好感度
  role_id?: number
  affection_value?: number
  // 道具
  item_name?: string
  // 服装
  costume_role_id?: number
  costume_name?: string[]
}

export interface Chapter {
  id: number
  title: string
}

export interface GameNode {
  id?: number
  game_id: number
  title: string
  description: string
  dialogues: Dialogues[]
  role_ids: string[]
  ending_role_ids?: number[] | null // 结尾参与角色：null=未启用（沿用场景角色），数组=启用
  prologue: string
  prologue_role_id?: number
  node_type: string
  is_naming_node?: boolean        // 命名节点：进入时玩家输入即主控姓名
  input_options?: InputOption[]   // 选项玩法：点击填入输入框灰色前缀的文案列表
  expanded?: boolean
  bg_url: string
  music_url: string
  bg_resource_id?: number
  music_resource_id?: number
  bg_display_name?: string
  music_display_name?: string
  game_chapter_id?: number
  version?: string // 节点版本
  user_special?: RoleConfig[]
  play_target?: PlayTarget
  egg?: Egg[] | EggV2[] // 支持 v1.0 和 v2.0
  story_background?: string
  hidden_ui?: boolean // 是否提示隐藏UI
  current_plot?: string
  current_plot_new?: CurrentPlotNew
  user_messages_new?: UserMessageNew[] // v2.0 用户消息处理
  output_examples?: OutputExample[]
  output_examples_json?: OutputExampleJson[]
  chapter_reward?: Reward[] // 章节完成奖励列表
  process?: ProcessConfig[] | ProcessConfigV2[] // 支持 v1.0 和 v2.0
  limit_turn?: number // 回合数限制
  node_task?: string // 攻略方式
  image_prompt?: string // 图片提示词
  jumpToOrder?: number // 用于跳转顺序的临时属性（已废弃）
  jumpTargetId?: number | string // 跳转目标节点ID（用于下拉选择）
  moveToPosition?: number | string | null // 用于下拉框移动节点位置（'top', 'bottom', 或索引号）
  dialogue_text?: string // 固定节点的剧情文本（前端编辑用，不发送到后端）
  entry_conditions?: EntryCondition[] // 进入条件
  graph_x?: number | null // 画布X坐标
  graph_y?: number | null // 画布Y坐标
  next_node_id?: number | null // 固定节点的下一个节点ID
  ui_show_next_node?: UiShowNextNode | null
  max_depth?: number // 节点在图谱中的深度
  level?: number // 节点层级（0=主线，每深一层+1）
  // 固定节点奖励（节点级别）
  award_conf?: RewardConfig[]
  // AI节点选项
  options?: NodeOption[]
  // 进入节点时是否自动展示选项
  show_options?: boolean
  is_be?: boolean // BE 结局节点：走到尽头弹回滚窗，否则弹催更窗
}

// 获取所有的节点
export async function getChapters(gameId: number): Promise<{data: Chapter[], message: string}> {
  const response = await service.get<{data: Chapter[], message: string}>('/api/nodes/chapter_list', {params: {game_id: gameId}})
  return response.data
}

// 获取节点列表，可按章节过滤
export async function getNodes(gameId: number, chapterId?: number): Promise<{data: GameNode[], message: string}> {
  const params: Record<string, unknown> = { game_id: gameId }
  if (chapterId) params.chapter_id = chapterId
  const response = await serviceGo.get<{data: GameNode[], message: string}>('/api_v2/nodes', { params })
  return response.data
}

// 获取单个节点
export async function getNode(id: number): Promise<GameNode> {
  const response = await serviceGo.get(`/api_v2/nodes/${id}`)
  return response.data
}

// 创建节点
export async function createNode(data: GameNode): Promise<GameNode> {
  const response = await serviceGo.post('/api_v2/nodes', data)
  return response.data
}

// 更新节点
export async function updateNode(id: number, data: GameNode): Promise<GameNode> {
  const response = await serviceGo.put(`/api_v2/nodes/${id}`, data)
  return response.data
}

// 删除节点
export async function deleteNode(id: number): Promise<void> {
  await serviceGo.delete(`/api_v2/nodes/${id}`)
}


// 获取用户已经玩过的节点列表
export async function getUserPlayNodes(gameId: number): Promise<GameNode[]>{
  const response = await service.get<GameNode[]>('/api/nodes/play_node_list', {params: {"game_id": gameId}})
  return response.data
}

// 跳转重玩章节
export async function retryPlayedNode(node_id: number): Promise<void>{
  await service.post<GameNode[]>('/api/nodes/retry_node', {node_id: node_id})
}

// 删除节点信息
export async function deleteRecordNode(record_id: number, node_id: number, is_socket: boolean): Promise<void>{
  console.log(`is socket is: `, is_socket)
  await service.post("/api/nodes/del_record_node",
      {record_id: record_id,
            node_id: node_id,
            client: 'wx_mini',
            is_socket: is_socket})
}

// 节点变更排序
export async function changeNodeOrder(node_ids: (number|undefined)[]){
  await serviceGo.post("/api_v2/nodes/save_node_sort", {node_ids: node_ids})
}

// 批量保存节点画布位置
export async function batchSavePositions(
  positions: Array<{ node_id: number; graph_x: number; graph_y: number }>
): Promise<void> {
  await serviceGo.post('/api_v2/nodes/batch_positions', { positions })
}

// 自动补全节点内容
export async function autoCompleteNode(data: {
  node_id?: number
  game_id: number
  plot_start: string
  plot_process: string
  plot_end: any
  prologue: string
  node_task: string
}): Promise<{
  story_background: string
  role_ids: number[]
  limit_turn: number | null
  process: any[]
  egg: any[]
  user_messages_new: any[]
}> {
  const response = await serviceGo.post('/api_v2/nodes/auto_complete', data)
  return response.data
}